import type {
  ClientOrderLineDto, ReconcileClientOrderBody, ReconcileFreeLine,
  SupplierLineStatus, SupplierOrderLineDto,
} from '../../../lib/api/v2/planning'

export interface ReconciledClientLine {
  clientLine: ClientOrderLineDto
  /** The supplier line tracking it, when checked. */
  tracked: SupplierOrderLineDto | null
}

export interface ClientOrderReconciliation {
  rows: ReconciledClientLine[]
  /** Supplier lines added by hand, or whose source vanished (quote amendment — P2, MS-07). */
  manual: SupplierOrderLineDto[]
}

/**
 * Reconciles the client order against the tracked supplier lines (SOLY-PO-001 MS-03).
 * Matching is by SourceOrderLineId; an orphaned source (OrderLine.Id churn on amendment —
 * the P2 bug fixed in MS-07) degrades gracefully to a "manual" line, never an error.
 * A CANCELLED tracked line no longer covers its quote line (UX r2 arbitrage): the cancelled
 * decision re-opens the question — the registre checkbox re-arms and a re-check creates a
 * fresh item, the cancelled one staying in history. A live match always wins over cancelled.
 */
export function reconcileClientOrder(
  clientLines: ClientOrderLineDto[],
  supplierLines: SupplierOrderLineDto[],
): ClientOrderReconciliation {
  const bySource = new Map<string, SupplierOrderLineDto>()
  for (const l of supplierLines) {
    if (!l.sourceOrderLineId) continue
    const current = bySource.get(l.sourceOrderLineId)
    if (!current || (current.lineStatus === 'Cancelled' && l.lineStatus !== 'Cancelled')) {
      bySource.set(l.sourceOrderLineId, l)
    }
  }

  const clientIds = new Set(clientLines.map((c) => c.id))

  return {
    rows: clientLines.map((clientLine) => {
      const match = bySource.get(clientLine.id) ?? null
      return { clientLine, tracked: match && match.lineStatus !== 'Cancelled' ? match : null }
    }),
    manual: supplierLines.filter((l) => !l.sourceOrderLineId || !clientIds.has(l.sourceOrderLineId)),
  }
}

// ── Registre rework (SOLY-PO-001) — kind-aware rendering + staged diff ────────

/**
 * Whether the line can be put on the purchase list. The rule is the server's (SOLY-IMPORT-003 S11): a
 * line set to « Acheter » — a subcontracted « Pose » too — or a product nobody qualified yet; never
 * « Fabriquer » or « Aucun ». Against an API older than S11 (no flag), only a `product_item` is. A
 * line that is not commandable is shown read-only so nothing is forgotten.
 */
export function isCommandable(line: Pick<ClientOrderLineDto, 'kind' | 'commandable'>): boolean {
  return line.commandable ?? line.kind === 'product_item'
}

/** A tracked supplier line is removable (untickable) only while it hasn't been ordered yet. */
const REMOVABLE_LINE_STATUSES: ReadonlySet<SupplierLineStatus> = new Set<SupplierLineStatus>(['ToOrder', 'OnHold'])

export function isLineRemovable(line: SupplierOrderLineDto): boolean {
  return REMOVABLE_LINE_STATUSES.has(line.lineStatus)
}

/** A staged free-line add in the local draft (before it is committed via reconcile). */
export interface DraftFreeLine extends ReconcileFreeLine {
  /** Local-only key so the UI can list / remove a staged free line before it has a server id. */
  draftKey: string
}

/**
 * The separator the title and the detail are joined with when a line is read as one string. It is
 * also what the pre-2026-09-08 code wrote INTO the description column, which is how a legacy row is
 * recognised and unfolded.
 */
const JOIN_SEPARATOR = '—'

/**
 * The full orderable text of a client line, for DISPLAY only (MS-14 item 1): the user must see
 * everything without reopening the devis. Never send this to the server — the supplier line stores
 * title and description in their own columns since V108, and folding them together overflowed
 * Description in production (2026-09-08). Use supplierLineTextOf for a line whose two fields are
 * already apart.
 */
export function fullClientLineText(line: ClientOrderLineDto): string {
  const detail = line.description?.trim()
  return detail ? `${line.title} ${JOIN_SEPARATOR} ${detail}` : line.title
}

/**
 * The detail of a line, with the title stripped off the front when it is still folded in there.
 *
 * EVERY row written before this change has the title in BOTH columns: `title` on its own and
 * `${title} — ${detail}` in `description`. Reading the two naively prints the title twice on every
 * line already in the database, so the prefix comes off here, once, for every surface.
 * Returns null when nothing is left — a free line with no detail stores its title in both columns.
 */
export function supplierLineDetailOf(line: { title?: string | null; description: string }): string | null {
  const title = line.title?.trim()
  const detail = line.description.trim()
  if (!title) return detail || null
  // Match the EXACT join the old code produced, separator included. A looser prefix test would
  // eat a real description that merely opens with the same word (title « Évier », description
  // « Éviers assortis en inox »).
  if (detail === title) return null
  if (!detail.startsWith(title)) return detail
  // Match on the SEPARATOR, not on a fixed number of spaces: the old join used the client line's
  // untrimmed title while the server stored it trimmed, so a title with a trailing space left a
  // double space in the description.
  const rest = detail.slice(title.length).trimStart()
  return rest.startsWith(JOIN_SEPARATOR) ? rest.slice(JOIN_SEPARATOR.length).trim() || null : detail
}

/**
 * The one-line reading of a line that carries its two fields apart. Lines created before V108 have
 * no title and kept the joined text in description, so the title is optional.
 */
export function supplierLineTextOf(line: { title?: string | null; description: string }): string {
  const title = line.title?.trim()
  const detail = supplierLineDetailOf(line)
  if (!title) return detail ?? ''
  return detail ? `${title} ${JOIN_SEPARATOR} ${detail}` : title
}

/** Live free lines (no source, not cancelled) — the registre is now their single remove point (MS-14). */
export function liveFreeLines(supplierLines: SupplierOrderLineDto[]): SupplierOrderLineDto[] {
  return supplierLines.filter((l) => !l.sourceOrderLineId && l.lineStatus !== 'Cancelled')
}

/**
 * Live tracked lines whose source order line vanished — a quote amendment from BEFORE the MS-07
 * lineage fix orphaned them. They are real ordered/to-order items and MUST stay visible in the
 * registre (they used to be silently dropped from every list); post-fix amendments no longer
 * produce new ones.
 */
export function orphanedTrackedLines(
  clientLines: ClientOrderLineDto[],
  supplierLines: SupplierOrderLineDto[],
): SupplierOrderLineDto[] {
  const clientIds = new Set(clientLines.map((c) => c.id))
  return supplierLines.filter(
    (l) => l.sourceOrderLineId && !clientIds.has(l.sourceOrderLineId) && l.lineStatus !== 'Cancelled',
  )
}

export interface RegistryDiffInput {
  /** All client order lines (any kind). */
  clientLines: ClientOrderLineDto[]
  /** Current tracked supplier lines (the server truth, incl. cancelled — filtered internally). */
  supplierLines: SupplierOrderLineDto[]
  /** sourceOrderLineIds the user wants tracked (ticked commandable lines). */
  desiredSourceIds: ReadonlySet<string>
  /** Free lines staged locally (not yet committed). */
  draftFreeLines: DraftFreeLine[]
  /** Per-source quantity override for staged adds — defaults to the client line quantity (MS-14). */
  desiredQuantities?: ReadonlyMap<string, number | null>
  /** Detached committed lines (free OR orphaned tracked) the user staged for removal here. */
  removedDetachedLineIds?: ReadonlySet<string>
}

/**
 * Computes the staged diff between the DESIRED tracking set (ticked commandable client lines + staged
 * free lines) and the CURRENT tracked supplier lines — a pure function, committed in one reconcile call.
 *
 *   - `adds`: a commandable client line is desired but has no live tracking line yet.
 *   - `removeLineIds`: a live, source-matched tracking line whose source is no longer desired — and that
 *     is still removable (ToOrder/OnHold). Locked lines (Assigned/Received) are NEVER emitted: they
 *     can't be unticked in the UI and the backend would reject them (LINE_NOT_REMOVABLE).
 *   - `freeLines`: the staged free lines, title and description kept apart.
 *
 * Cancelled lines are ignored (they no longer cover their source — a re-tick is a fresh add).
 */
export function computeRegistryDiff({
  clientLines, supplierLines, desiredSourceIds, draftFreeLines, desiredQuantities, removedDetachedLineIds,
}: RegistryDiffInput): ReconcileClientOrderBody {
  // Live tracking line per source id (cancelled ignored — mirrors reconcileClientOrder's matching).
  const liveBySource = new Map<string, SupplierOrderLineDto>()
  for (const l of supplierLines) {
    if (!l.sourceOrderLineId || l.lineStatus === 'Cancelled') continue
    if (!liveBySource.has(l.sourceOrderLineId)) liveBySource.set(l.sourceOrderLineId, l)
  }

  const byClientId = new Map(clientLines.map((c) => [c.id, c]))

  const adds: { sourceOrderLineId: string; description: string; title?: string | null; quantity?: number | null }[] = []
  for (const sourceId of desiredSourceIds) {
    const clientLine = byClientId.get(sourceId)
    // Only commandable lines can be tracked; a non-commandable desired id is dropped defensively.
    if (!clientLine || !isCommandable(clientLine)) continue
    if (!liveBySource.has(sourceId)) {
      // Quantity carries from the client order line, unless the user adjusted it before validating.
      const quantity = desiredQuantities?.has(sourceId) ? desiredQuantities.get(sourceId)! : clientLine.quantity
      // Title and description travel apart: the line has a column for each since V108, and the
      // joined text overflowed Description in production. Description falls back to the title for
      // a client line that has none (it is NOT NULL server-side).
      adds.push({
        sourceOrderLineId: sourceId,
        description: clientLine.description?.trim() || clientLine.title,
        title: clientLine.title,
        quantity,
      })
    }
  }

  const removeLineIds: string[] = []
  for (const [sourceId, line] of liveBySource) {
    if (!desiredSourceIds.has(sourceId) && isLineRemovable(line)) {
      removeLineIds.push(line.id)
    }
  }
  // Item 3 (MS-14, extended MS-07): detached committed lines the user dropped here — free lines
  // (no source) AND orphaned tracked lines (source vanished on a pre-fix amendment). The registre
  // is their single remove point. Only removable ones can leave (mirrors the server guard).
  if (removedDetachedLineIds && removedDetachedLineIds.size > 0) {
    for (const l of supplierLines) {
      const detached = !l.sourceOrderLineId || !byClientId.has(l.sourceOrderLineId)
      if (detached && removedDetachedLineIds.has(l.id) && isLineRemovable(l)) {
        removeLineIds.push(l.id)
      }
    }
  }

  const freeLines: ReconcileFreeLine[] = draftFreeLines.map(
    ({ description, supplierId, quantity, title }) => ({ description, supplierId, quantity, title }),
  )

  return { adds, removeLineIds, freeLines }
}

/** True when a staged diff actually changes something (drives the « Valider » footer + nav guard). */
export function isDiffEmpty(diff: ReconcileClientOrderBody): boolean {
  return diff.adds.length === 0 && diff.removeLineIds.length === 0 && diff.freeLines.length === 0
}
